"use client";

import { Fragment, useEffect, useState, useRef, useCallback } from 'react'
import { Dialog, Disclosure, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon, PlusIcon, MagnifyingGlassIcon, MinusIcon } from '@heroicons/react/20/solid'
import Image from "next/image";
import Link from "next/link";
import Pagination from "@/components/shared/pagination"
import { useRouter } from 'next/navigation';
import parse from 'html-react-parser';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const options = {
  replace: (node) => {
    if (node.name === 'img') {
      // console.log(node.attribs)
      return <Image src={node.attribs.src} width={node.attribs?.width || 500} height={1000} alt={node.attribs.alt || "MF Asia Product"} />;
    }
  }
};

export default function CatalogBody(props: any) {
  
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null);
  const { 
    meta,
    slug,
    category,
    collection,
    products,
    categories,
    collections, 
    industries,
    tags,
    brands,
    page,
    keyword,
    selected_brand,
    selected_industry,
    selected_tags
  } = props;

  const [brandData, setBrandData] = useState(selected_brand)
  const [industryData, setIndustryData] = useState(selected_industry)
  const [tagsData, setTagsData] = useState(selected_tags)
  const [pageData, setPageData] = useState(page)
  const [keywordData, setKeywordData] = useState(keyword)
  const router = useRouter();
  const loadedRef = useRef(false);

  const navigate = useCallback((pageData, brandData, tagsData, industryData) => {
    router.push(
      `/catalog/${slug}?page=${pageData}${brandData ? `&brand=${brandData}` : ''}${
        tagsData.length > 0 ? `&tags=${tagsData}` : ''
      }${industryData ? `&industry=${industryData}` : ''}`
    );
  }, [router, slug]);

  useEffect(() => {
    if (loadedRef.current) {
      navigate(pageData, brandData, tagsData, industryData);
    } else {
      loadedRef.current = true;
    }
  }, [pageData, brandData, tagsData, industryData, navigate]);

  useEffect(() => {
    setKeywordData(keyword);
  }, [slug, keyword]);

  const handlePageChange = ({ selected }) => {
    const newPageData = selected + 1
    if(newPageData == pageData ) return;
    setPageData(newPageData)
  };

  const handleBrandChange = (event) => {
    if(event.target.value != brandData){
      setBrandData(event.target.value);
    }else{
      setBrandData('');
    }
    setPageData(1)
  };

  const handleSearchChange = (event) => {
    setKeywordData(event.target.value);
    setPageData(1);
  };

  // const handleIndustryChange = (event) => {
  //   if(event.target.value != industryData){
  //     setIndustryData(event.target.value);
  //   }else{
  //     setIndustryData('');
  //   }
  //   setPageData(1)
  // };

  const handleTagChange = (value, checked) => {
    if (checked) {
      setTagsData([...tagsData, value]);
    } else {
      setTagsData(tagsData.filter((item) => item !== value));
    }
    setPageData(1)
  };

  const breadcrumbStructuredData = category ? 
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': "ListItem",
        position: 1,
        name: "Catalog",
        item: "https://mf.asia/catalog"
      },
      {
        '@type': "ListItem",
        position: 2,
        name: collection ? collection.title : category.title,
        item: `https://mf.asia/${collection ? collection.slug : category.slug}`
      }]
    } : {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': "ListItem",
        position: 1,
        name: "Catalog",
        item: "https://mf.asia/catalog"
      }]
    }

  return (
    <>
      <div>
        {/* Mobile filter dialog */}
        <Transition.Root show={mobileFiltersOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 sm:hidden __variable_89a9b2 font-sans" onClose={() => setMobileFiltersOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-6 shadow-xl font-sans">
                  <div className="flex items-center justify-between px-4">
                    <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                    <button
                      type="button"
                      className="-mr-2 flex h-10 w-10 items-center justify-center p-2 text-gray-400 hover:text-gray-500"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      <span className="sr-only">Close menu</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Filters */}
                  <form className="mt-4">
                    <Disclosure as="div" defaultOpen className="border-t border-gray-200 pt-4 pb-4">
                      {({ open }) => (
                        <fieldset>
                          <legend className="w-full px-2">
                            <Disclosure.Button className="flex w-full items-center justify-between p-2 text-gray-400 hover:text-gray-500">
                              <span className="text-sm font-medium text-gray-900"> Categories </span>
                              <span className="ml-6 flex h-7 items-center">
                                <ChevronDownIcon
                                  className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </legend>
                          <Disclosure.Panel className="px-4 pb-2">
                            <div className="space-y-6">
                              <ul role="list" className="pt-2 space-y-4 border-gray-200 text-sm font-medium text-gray-900">
                                <li key='all-products'>
                                  <Link href={`/catalog`} onClick={() => setMobileFiltersOpen(false)} className={`${slug == '' ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                                    All Products
                                  </Link>
                                </li>
                                {categories.map((category) => (
                                  <li key={category.slug}>
                                    <Link href={`/catalog/${category.slug}`} onClick={() => setMobileFiltersOpen(false)} className={`${slug == category.slug ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                                      {category.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </Disclosure.Panel>
                        </fieldset>
                      )}
                    </Disclosure>

                    <Disclosure as="div" defaultOpen className="border-t border-gray-200 pt-4 pb-4">
                      {({ open }) => (
                        <fieldset>
                          <legend className="w-full px-2">
                            <Disclosure.Button className="flex w-full items-center justify-between p-2 text-gray-400 hover:text-gray-500">
                              <span className="text-sm font-medium text-gray-900"> Collections </span>
                              <span className="ml-6 flex h-7 items-center">
                                <ChevronDownIcon
                                  className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </legend>
                          <Disclosure.Panel className="px-4 pb-2">
                            <div className="space-y-6">
                              <ul role="list" className="pt-2 space-y-4 border-gray-200 text-sm font-medium text-gray-900">
                                {collections.map((collection) => (
                                  <li key={collection.slug}>
                                    <Link href={`/catalog/${collection.slug}`} onClick={() => setMobileFiltersOpen(false)} className={`${slug == collection.slug ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                                      {collection.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </Disclosure.Panel>
                        </fieldset>
                      )}
                    </Disclosure>

                    <Disclosure as="div" defaultOpen={!!brandData} className="border-t border-gray-200 pt-4 pb-4">
                      {({ open }) => (
                        <fieldset>
                          <legend className="w-full px-2">
                            <Disclosure.Button className="flex w-full items-center justify-between p-2 text-gray-400 hover:text-gray-500">
                              <div className="flex space-x-1">
                                <span className="text-sm font-medium text-gray-900"> Brands </span>
                                {brandData && 
                                  <button className="ml-6 flex items-center cursor-pointer" onClick={() => {setBrandData(''); setMobileFiltersOpen(false)}}>
                                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                  </button>
                                }
                              </div>
                              <span className="ml-6 flex h-7 items-center">
                                <ChevronDownIcon
                                  className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </legend>
                          <Disclosure.Panel className="px-4 pt-2 pb-2">
                            <div className="space-y-4">
                            {brands.map((option, optionIdx) => (
                              <div key={option.slug} className="flex items-center">
                                <input
                                  id={`${option.slug}-${optionIdx}`}
                                  name={option.slug}
                                  value={option.slug}
                                  type="checkbox"
                                  checked={selected_brand === option.slug}
                                  onChange={handleBrandChange}
                                  onClick={() => setMobileFiltersOpen(false)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label htmlFor={`${option.slug}-${optionIdx}`} className="ml-3 text-sm text-gray-600 cursor-pointer	">
                                  {option.name}
                                </label>
                              </div>
                            ))}
                            </div>
                          </Disclosure.Panel>
                        </fieldset>
                      )}
                    </Disclosure>

                    <Disclosure as="div" defaultOpen={tagsData && tagsData.length > 0} className="border-t border-gray-200 pt-4 pb-4">
                      {({ open }) => (
                        <fieldset>
                          <legend className="w-full px-2">
                            <Disclosure.Button className="flex w-full items-center justify-between p-2 text-gray-400 hover:text-gray-500">
                              <div className="flex space-x-1">
                                <span className="text-sm font-medium text-gray-900"> Tags </span>
                                { tagsData && tagsData.length > 0 &&
                                  <button className="ml-6 flex items-center cursor-pointer" onClick={() => setTagsData([])}>
                                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                  </button>
                                }
                              </div>
                              <span className="ml-6 flex h-7 items-center">
                                <ChevronDownIcon
                                  className={classNames(open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform')}
                                  aria-hidden="true"
                                />
                              </span>
                            </Disclosure.Button>
                          </legend>
                          <Disclosure.Panel className="px-4 pt-2 pb-2">
                            <div className="space-y-4">
                            {tags.map((option, optionIdx) => (
                              <div key={option} className="flex items-center">
                                <input
                                  id={`${option}-${optionIdx}`}
                                  name={`${option}[]`}
                                  value={option}
                                  checked={selected_tags.includes(option)}
                                  onChange={(e) => handleTagChange(option, e.target.checked)}
                                  // onChange={handleTagChange}
                                  type="checkbox"
                                  onClick={() => setMobileFiltersOpen(false)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label htmlFor={`${option}-${optionIdx}`} className="ml-3 text-sm text-gray-600 cursor-pointer	">
                                  {option}
                                </label>
                              </div>
                            ))}
                            </div>
                          </Disclosure.Panel>
                        </fieldset>
                      )}
                    </Disclosure>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        <main className="mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8">
          <div className="pt-24">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900"> { collection?.title || category.title } </h1>

            {!collection && <p className="mt-4 text-base text-gray-500">
              { category.description || "From custom t-shirts and workwear to promotional products and even embroidery, MF has the perfect product for your team, school, company or event." }
            </p>}

            {collection?.content && <div className='ql-snow mt-3'>
              {/* <h2 className="text-base font-bold leading-normal tracking-tight text-gray-900">
                { parse(collection.description, options) }
              </h2> */}
              <div className='mt-3 relative ql-editor'>
                
                { parse(collection.content, options) }
              </div>
            </div>}
          </div>

          <div className="pt-12 pb-24 sm:grid sm:grid-cols-4 lg:grid-cols-4 sm:gap-x-4 lg:gap-x-8 xl:grid-cols-6">
            <aside>
              <h2 className="sr-only">Filters</h2>
  

              <form ref={formRef} className="space-y-5 divide-y divide-gray-200">
                <div>
                  <fieldset>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        tabIndex={0}
                        id="search"
                        name="keyword"
                        className="block w-full rounded-md border-0 bg-white py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                        placeholder="Search"
                        value={keywordData}
                        onChange={handleSearchChange}
                        onBlur={(e) => {formRef?.current?.submit()}}
                        type="search"
                      />
                    </div>
                  </fieldset>
                </div>
                <div className="hidden sm:block space-y-5 divide-y divide-gray-200">
                  <div className='pt-5'>
                    <fieldset>
                      <div className="flex items-center justify-between">
                        <Link href={`/catalog`} className="flex items-center cursor-pointer">
                          <legend className="block text-sm font-medium text-gray-900"> Collections </legend>
                        </Link>
                        {/* {brandData &&  */}
                        {/* <Link href={`/catalog`} className="ml-6 flex items-center cursor-pointer">
                          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                        </Link> */}
                        {/* } */}
                      </div>
                      <ul role="list" className="pt-4 space-y-4 border-gray-200 text-sm font-medium text-gray-900">
                        {/* <li key='all-products'>
                          <Link href={`/catalog`} className={`${slug == '' ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                            All Collections
                          </Link>
                        </li> */}
                        {collections.map((collection) => (
                          <li key={collection.slug}>
                            <Link href={`/catalog/${collection.slug}`} className={`${slug == collection.slug ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                              {collection.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </fieldset>
                  </div>
                  <div className='pt-5'>
                    <fieldset>
                      <Disclosure as="div" defaultOpen={!collection} className="">
                        {({ open }) => (
                          <>
                            <h3 className="-my-3 flow-root">
                              <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                                <legend className="block text-sm font-medium text-gray-900"> Categories </legend>

                                
                                <div className="ml-6 flex items-center">
                                  {open ? (
                                    <MinusIcon className="h-5 w-5" aria-hidden="true" />
                                  ) : (
                                    <PlusIcon className="h-5 w-5" aria-hidden="true" />
                                  )}
                                </div>
                              </Disclosure.Button>
                            </h3>
                            <Disclosure.Panel>
                              <ul role="list" className="pt-4 space-y-4 border-gray-200 text-sm font-medium text-gray-900">
                                <li key='all-products'>
                                  <Link href={`/catalog`} className={`${slug == '' ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                                    All Products
                                  </Link>
                                </li>
                                {categories.map((category) => (
                                  <li key={category.slug}>
                                    <Link href={`/catalog/${category.slug}`} className={`${slug == category.slug ? 'text-blue-600' : 'text-gray-600'} text-sm`}>
                                      {category.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </fieldset>
                  </div>
                  <div className='pt-5'>
                    <fieldset>
                      <Disclosure as="div" defaultOpen={!!brandData} className="">
                        {({ open }) => (
                          <>
                            <h3 className="-my-3 flow-root">
                              <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                                <legend className="block text-sm font-medium text-gray-900"> Brands </legend>

                                
                                <div className="ml-6 flex items-center">
                                  {open ? (
                                    brandData ? 
                                      <XMarkIcon aria-hidden="true" className="h-5 w-5 cursor-pointer" onClick={() => setBrandData('')}>
                                      </XMarkIcon> : <MinusIcon className="h-5 w-5" aria-hidden="true" />
                                  ) : (
                                    <PlusIcon className="h-5 w-5" aria-hidden="true" />
                                  )}
                                </div>
                              </Disclosure.Button>
                            </h3>
                            <Disclosure.Panel className="pt-4">
                              <div className="space-y-4">
                                {brands.map((option, optionIdx) => (
                                  <div key={option.slug} className="flex items-center">
                                    <input
                                      id={`filter-${option.slug}-${optionIdx}`}
                                      name={`${option.slug}[]`}
                                      value={option.slug}
                                      type="checkbox"
                                      checked={selected_brand === option.slug}
                                      onChange={handleBrandChange}
                                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label
                                      htmlFor={`filter-${option.slug}-${optionIdx}`}
                                      className="ml-3 text-sm text-gray-600"
                                    >
                                      {option.name}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                      {/* <div className="flex items-center justify-between">
                        <legend className="block text-sm font-medium text-gray-900"> Brands </legend>
                        {brandData && 
                          <button className="ml-6 flex items-center cursor-pointer" onClick={() => setBrandData('')}>
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        }
                      </div>
                      <div className="space-y-3 pt-4">
                        {brands.map((option, optionIdx) => (
                          <div key={option.slug} className="flex items-center">
                            <input
                              id={`${option.slug}-${optionIdx}`}
                              name={option.slug}
                              value={option.slug}
                              type="checkbox"
                              checked={selected_brand === option.slug}
                              onChange={handleBrandChange}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor={`${option.slug}-${optionIdx}`} className="ml-3 text-sm text-gray-600 cursor-pointer	">
                              {option.name}
                            </label>
                          </div>
                        ))}
                      </div> */}
                    </fieldset>
                  </div>
                  {/* <div className='pt-5'>
                    <fieldset>
                      <Disclosure as="div" defaultOpen={!!industryData} className="">
                        {({ open }) => (
                          <>
                            <h3 className="-my-3 flow-root">
                              <Disclosure.Button className="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                                <legend className="block text-sm font-medium text-gray-900"> Industries </legend>

                                
                                <div className="ml-6 flex items-center">
                                  {open ? (
                                    industryData ? 
                                      <XMarkIcon aria-hidden="true" className="h-5 w-5 cursor-pointer" onClick={() => setIndustryData('')}>
                                      </XMarkIcon> : <MinusIcon className="h-5 w-5" aria-hidden="true" />
                                  ) : (
                                    <PlusIcon className="h-5 w-5" aria-hidden="true" />
                                  )}
                                </div>
                              </Disclosure.Button>
                            </h3>
                            <Disclosure.Panel className="pt-4">
                              <div className="space-y-4">
                                {industries.map((option, optionIdx) => (
                                  <div key={option.slug} className="flex items-center">
                                    <input
                                      id={`filter-${option.id}-${optionIdx}`}
                                      name={`${option.id}[]`}
                                      value={option.slug}
                                      type="checkbox"
                                      checked={selected_industry === option.slug}
                                      onChange={handleIndustryChange}
                                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label
                                      htmlFor={`filter-${option.id}-${optionIdx}`}
                                      className="ml-3 text-sm text-gray-600"
                                    >
                                      {option.name}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </fieldset>
                  </div> */}
                  <div className='pt-5'>
                    <fieldset>
                      <div className="flex items-center justify-between">
                        <legend className="block text-sm font-medium text-gray-900"> Tags </legend>
                        { tagsData && tagsData.length > 0 &&
                          <button className="ml-6 flex items-center cursor-pointer" onClick={() => setTagsData([])}>
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        }
                      </div>
                      <div className="space-y-3 pt-4">
                        <span className="space-y-2 prose-lg mt-4 text-gray-500">
                          {tags?.map((tag) => (
                            <Link  key={tag} href={`/catalog?tags=${tag}`}>
                              <span className="inline-flex items-center rounded-md bg-blue-50 mr-2 px-2.5 py-0.5 text-sm font-medium text-blue-600">
                                {tag}
                              </span>
                            </Link>
                          ))}
                        </span>
                      </div>
                      {/* <div className="space-y-3 pt-6">
                        {tags.map((option, optionIdx) => (
                          <div key={option} className="flex items-center">
                            <input
                              id={`${option}-${optionIdx}`}
                              name={`${option}[]`}
                              value={option}
                              checked={selected_tags.includes(option)}
                              onChange={(e) => handleTagChange(option, e.target.checked)}
                              // onChange={handleTagChange}
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor={`${option}-${optionIdx}`} className="ml-3 text-sm text-gray-600 cursor-pointer	">
                              {option}
                            </label>
                          </div>
                        ))}
                      </div> */}
                    </fieldset>
                  </div>
                </div>
              </form>

              <button
                type="button"
                className="inline-flex mt-3 items-center sm:hidden"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <span className="text-sm font-medium text-gray-700">Filters</span>
                <PlusIcon className="ml-1 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              </button>
            </aside>

            {products && products.length > 0 ? <section aria-labelledby="product-heading" className="mt-6 sm:col-span-3 lg:col-span-3 lg:mt-0 xl:col-span-5">
              <h2 id="product-heading" className="sr-only">
                Products
              </h2>

              <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3 xs:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:gap-x-6 xl:grid-cols-4">
                {products.map((product) => (
                  <div
                    key={product.slug}
                    className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
                  >
                    <div className="aspect-w-3 aspect-h-3 bg-gray-200 group-hover:opacity-75 sm:aspect-none">
                      {product.imageSrc && <Image
                        src={product.imageSrc || ''}
                        alt={product.name}
                        width={512}
                        height={512}
                        className="h-full w-full object-cover object-center sm:h-full sm:w-full"
                      />}
                    </div>
                    <div className="flex flex-1 flex-col space-y-2 p-4">
                      <h3 className="text-sm font-medium text-gray-900">
                        <Link href={`/products/${product.slug}`}>
                          <span aria-hidden="true" className="absolute inset-0" />
                          {product.name}
                        </Link>
                      </h3>
                      {/* <p className="text-sm text-gray-500">{product.description}</p> */}
                      {/* <div className="flex flex-1 flex-col justify-end">
                        <p className="text-sm italic text-gray-500">{product.options}</p>
                        <p className="text-base font-medium text-gray-900">{product.price}</p>
                      </div> */}
                    </div>
                  </div>
                ))}
              </div>
              <Pagination currentPage={ page }
                totalCount={meta.total_count}
                pageSize={meta.per_page}
                onPageChange={handlePageChange}
              />
            </section> : <p className="text-gray-900 mt-5"> No Products Yet </p>}
          </div>
        </main>
      </div>
    </>
  )
}